import type { ViewColumn, ViewRow } from "@/lib/boardView";
import type { SortSpec } from "@/lib/types";

// Column types we offer a sort control for.
export const SORTABLE_TYPES = ["text", "long_text", "status", "number", "date", "checkbox", "link"];

// Synthetic sort key: order ticket boards by the row's human ticket number
// (IT-<n>) rather than any column cell. Not a real column id — handled before
// the column lookup in sortRows and injected as a virtual sort option.
export const TICKET_NUMBER_SORT_ID = "__ticket_number__";

// A comparable key for one row in a column. status sorts by the column's own
// option order; numbers numeric; dates by time; everything else case-folded text.
function sortKey(row: ViewRow, col: ViewColumn): number | string | null {
  const v = row.raw?.cells?.find((c) => c.column_def_id === col.id)?.value;
  if (v == null) return null;
  switch (col.type) {
    case "status": {
      const idx = (col.options ?? []).findIndex((o) => o.id === v);
      return idx < 0 ? Number.MAX_SAFE_INTEGER : idx;
    }
    case "number":
      return typeof v === "number" ? v : null;
    case "checkbox":
      return v === true ? 1 : 0;
    case "date":
      return typeof v === "string" ? new Date(v).getTime() : null;
    case "link":
      if (v && typeof v === "object") {
        const lv = v as { label?: string; url?: string };
        return (lv.label ?? lv.url ?? "").toLowerCase();
      }
      return "";
    default:
      return typeof v === "string" ? v.toLowerCase() : "";
  }
}

// Sort top-level rows by the chosen column (nulls always last, regardless of
// direction); each row's sub-items follow it in their existing order.
export function sortRows(
  rows: ViewRow[],
  sort: SortSpec | null | undefined,
  columns: ViewColumn[],
): ViewRow[] {
  if (!sort?.column_id) return rows;
  // The ticket-number sort has no backing column — resolve its key off the row.
  const isTicketSort = sort.column_id === TICKET_NUMBER_SORT_ID;
  const col = isTicketSort ? null : columns.find((c) => c.id === sort.column_id);
  if (!isTicketSort && !col) return rows;

  const tops = rows.filter((r) => !r.parentRowId);
  const kids = new Map<string, ViewRow[]>();
  for (const r of rows) {
    if (!r.parentRowId) continue;
    const arr = kids.get(r.parentRowId);
    if (arr) arr.push(r);
    else kids.set(r.parentRowId, [r]);
  }

  const dir = sort.dir === "desc" ? -1 : 1;
  const sorted = [...tops].sort((a, b) => {
    const ka = isTicketSort ? a.ticketNumber : sortKey(a, col!);
    const kb = isTicketSort ? b.ticketNumber : sortKey(b, col!);
    if (ka == null && kb == null) return 0;
    if (ka == null) return 1;
    if (kb == null) return -1;
    const base =
      typeof ka === "number" && typeof kb === "number"
        ? ka - kb
        : String(ka).localeCompare(String(kb), "de");
    return base * dir;
  });

  const out: ViewRow[] = [];
  for (const top of sorted) {
    out.push(top);
    const c = kids.get(top.id);
    if (c) out.push(...c);
  }
  return out;
}
